import type { TranslationPhrase } from '@src/types.ts/translation';

/**
 * PLAT-10266 (Card R2, 2026-07-29) — the ONE predicate both translated-column triggers
 * route on when deciding "is this selection a stale fragment to resolve, or a span to
 * fix?".
 *
 * Why it lives in its own module rather than inside either caller: the two triggers are
 * owned by different units and must agree, or the bug half-returns. `AlignedBlocksView`
 * owns right-click and already knew about staleness; `useSpanReplace` owns highlighting
 * and knew nothing about it — that asymmetry IS the defect (reproduced live in all three
 * views, logs/repro-stale-highlight-20260729/). A second copy of this rule would drift.
 * It is also placed here, not in `resolveStaleFragment.ts`, because that module imports
 * from `useSpanReplace` — putting it there would close an import cycle.
 */

/**
 * The CANONICAL stale key for a stored phrase: `min(originalIndices)`, or `null` when it
 * covers no source index.
 *
 * This is the key `staleLeads` and the resolve handlers use. It is deliberately NOT the
 * render model's `lead`: in Paragraph view `lead` is the first-RENDERED member's index,
 * which diverges from the canonical key when the covered lead word was itself edited —
 * keying the amber flag and resolve menu on `lead` there missed the flag entirely
 * (repaired in Card 6, and re-derived here so the highlight path cannot re-introduce it).
 *
 * `AlignedBlocksView` can fall back to `phrase.lead` because it holds a RENDER model;
 * this operates on the STORED `TranslationPhrase`, which has no such field. An
 * index-less phrase therefore yields `null` and is treated as "not a single stale
 * fragment", so the selection falls through to the span path — the conservative
 * direction, since routing an unkeyable phrase to a resolve action could act on the
 * wrong fragment.
 */
export function staleKeyOfPhrase(phrase: TranslationPhrase): number | null {
  return phrase.originalIndices.length > 0
    ? Math.min(...phrase.originalIndices)
    : null;
}

/**
 * The lead of the single stale fragment this selection covers, or `null`.
 *
 * `coveredPhrases` must be the **WIDENED** covered set (Segment view expands a selection
 * to the whole segment before authoring). Denys's ruling of 2026-07-28 is that
 * stale-vs-mixed is judged AFTER widening — he chose this knowing it means the whole
 * segment has to be stale in Segment view.
 *
 * Returns a lead ONLY when every covered phrase is stale AND they all collapse to one
 * canonical key. Two deliberate non-cases:
 *
 * - **Mixed** (stale + not stale) → `null`, so "Fix Translation" still wins. That is the
 *   ruling above.
 * - **Several distinct stale fragments** → `null`, so today's behavior is unchanged.
 *   Resolving all of them is Denys's decision 2, but the resolve menu and both resolve
 *   handlers still act on a single lead, so routing here would open a menu that silently
 *   acted on one of several highlighted fragments. Card R3 widens them to a set; until
 *   then the honest behavior is to leave this case alone.
 *
 * `staleLeads` absent or empty → `null`, which is what keeps the hook's two OTHER
 * consumers (the inline prose view and the translated captions view) byte-identical.
 */
export function singleStaleLeadOfSelection(
  coveredPhrases: readonly TranslationPhrase[],
  staleLeads: ReadonlySet<number> | undefined,
): number | null {
  if (!staleLeads || staleLeads.size === 0) return null;
  if (coveredPhrases.length === 0) return null;

  const keys = new Set<number>();
  for (const phrase of coveredPhrases) {
    const key = staleKeyOfPhrase(phrase);
    // A single non-stale (or unkeyable) member makes this not a clean single-stale
    // selection — stop rather than widening the intent past what the reviewer selected.
    if (key === null || !staleLeads.has(key)) return null;
    keys.add(key);
  }
  if (keys.size !== 1) return null;
  return [...keys][0];
}
